import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, TrendingUp, Users, Activity } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface League {
  id: string;
  name: string;
}

interface Team {
  id: string;
  user_id: string;
  league_id: string;
}

interface GameweekInfo {
  id: string;
  gw_number: number;
  lock_at: string;
  status: string;
}

interface Match {
  id: string;
  home_team: string;
  away_team: string;
  scheduled_time: string;
  status: string;
  home_team_logo?: string;
  away_team_logo?: string;
}

interface PlayerStatus {
  player_id: string;
  status: "injured" | "suspended" | "playing" | "finished";
  player_name: string;
  team: string;
}

interface QuickStats {
  projectedPoints: number;
  playersWithMatches: number;
  injuredCount: number;
  totalPlayers: number;
}

interface ContextWidgetsProps {
  team: Team;
  league: League;
  gameweek: GameweekInfo | null;
}

export function ContextWidgets({
  team,
  league,
  gameweek,
}: ContextWidgetsProps) {
  const [upcomingMatches, setUpcomingMatches] = useState<Match[]>([]);
  const [playerStatuses, setPlayerStatuses] = useState<PlayerStatus[]>([]);
  const [quickStats, setQuickStats] = useState<QuickStats>({
    projectedPoints: 0,
    playersWithMatches: 0,
    injuredCount: 0,
    totalPlayers: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchUpcomingMatches = async () => {
    try {
      const { data, error } = await supabase
        .from('matches')
        .select('*')
        .eq('league_id', league.id)
        .gte('scheduled_time', new Date().toISOString())
        .order('scheduled_time', { ascending: true })
        .limit(5);

      if (error) {
        console.error('Error fetching matches:', error);
        // Set empty array if matches can't be fetched (table might not exist yet)
        setUpcomingMatches([]);
        return;
      }
      setUpcomingMatches(data || []);
    } catch (error) {
      console.error('Error fetching matches:', error);
      setUpcomingMatches([]);
    }
  };

  const fetchPlayerStatuses = async () => {
    if (!gameweek) return;

    try {
      // Fetch roster slots with player info
      const { data, error } = await supabase
        .from('roster_slots')
        .select(`
          player_id,
          players (
            name,
            team,
            is_active
          )
        `)
        .eq('league_player_id', team.id)
        .eq('gameweek_id', gameweek.id);

      if (error) throw error;

      // Mock some statuses for demo purposes
      const statuses: PlayerStatus[] = (data || []).map((slot) => ({
        player_id: slot.player_id,
        status: slot.players?.is_active ? "playing" : "injured",
        player_name: slot.players?.name || "Unknown",
        team: slot.players?.team || "Unknown",
      }));

      setPlayerStatuses(statuses);

      // Calculate quick stats
      const totalPlayers = statuses.length;
      const injuredCount = statuses.filter(s => s.status === "injured").length;
      const playersWithMatches = statuses.filter(s => s.status === "playing").length;
      const projectedPoints = playersWithMatches * 6.5; // Mock calculation

      setQuickStats({
        projectedPoints,
        playersWithMatches,
        injuredCount,
        totalPlayers,
      });
    } catch (error) {
      console.error('Error fetching player statuses:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchUpcomingMatches(),
        fetchPlayerStatuses(),
      ]);
      setLoading(false);
    };

    fetchData();
  }, [team, league, gameweek]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "playing":
        return "default";
      case "finished":
        return "secondary";
      case "injured":
      case "suspended":
        return "destructive";
      default:
        return "outline";
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5" />
            Quick Stats
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-muted-foreground">Projected Points</div>
              <div className="text-lg font-mono font-bold">{quickStats.projectedPoints.toFixed(1)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Players Playing</div>
              <div className="text-lg font-mono font-bold">{quickStats.playersWithMatches}/{quickStats.totalPlayers}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Injured</div>
              <div className="text-lg font-mono font-bold text-destructive">{quickStats.injuredCount}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Active Players</div>
              <div className="text-lg font-mono font-bold text-success">{quickStats.totalPlayers - quickStats.injuredCount}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Matches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" />
            Upcoming Matches
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingMatches.length > 0 ? (
            <div className="space-y-3">
              {upcomingMatches.map((match) => (
                <div
                  key={match.id}
                  className="flex items-center justify-between p-2 rounded border"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div className="text-sm font-medium">
                      {match.home_team} vs {match.away_team}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(match.scheduled_time)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Fixtures will appear once the schedule is released
            </p>
          )}
        </CardContent>
      </Card>

      {/* Player Statuses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5" />
            Player Statuses
          </CardTitle>
        </CardHeader>
        <CardContent>
          {playerStatuses.length > 0 ? (
            <div className="space-y-2">
              {playerStatuses
                .filter(status => status.status !== "playing")
                .map((status) => (
                  <div
                    key={status.player_id}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex-1 truncate">
                      <div className="font-medium">{status.player_name}</div>
                      <div className="text-xs text-muted-foreground">{status.team}</div>
                    </div>
                    <Badge variant={getStatusBadgeVariant(status.status)} className="text-xs">
                      {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                    </Badge>
                  </div>
                ))}
              
              {playerStatuses.filter(s => s.status !== "playing").length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  All players are available
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No player information available
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}